/**
 * ProjectDetailPage - Project detail view with tasks and configurations
 *
 * Features:
 * - Project overview and metadata
 * - GitHub and Jira configurations (editable)
 * - Slack configuration
 * - Tickets list (one row per ticket, grouping its runs)
 */

import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, List } from 'lucide-react';
import { ProjectStatuses } from '@resolve-ai/shared';
import { LoadingState, ErrorMessage, Toast, Card } from '@/components/common';
import {
  AgentContextCard,
  GitHubConfigCard,
  JiraConfigCard,
  SlackConfigCard,
  TicketsTable,
  EditBranchModal,
  ManualImportTicket,
} from '@/components/project';
import { GitHubLogo } from '@/components/icons';
import { useProjectDetailPage } from '../hooks/useProjectDetailPage';

export function ProjectDetailPage() {
  const navigate = useNavigate();
  const { page, tickets, integrations, modal, actions, mutations, toast } = useProjectDetailPage();
  const { addRepo, editBranch, slack, jira, confirmations } = modal;
  const project = page.project;

  if ((page.error || !project) && !page.isLoading) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/projects')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Projects
        </button>
        <ErrorMessage message={page.error || 'Project not found'} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <LoadingState message="Loading project..." isLoading={page.isLoading} />
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/projects')}
          className="flex items-center font-caption gap-2 text-gray-500 dark:text-white-500 mb-[0.84rem]"
        >
          <ArrowLeft className="w-4.5 h-4.5" />
          Back to Projects
        </button>
        <div className="flex items-center gap-3">
          <span className="font-subtitle dark:text-white">{project?.name}</span>
          {project?.status === ProjectStatuses.ACTIVE && (
            <p className="flex px-2 py-1 gap-[0.38rem] items-center rounded-lg bg-green-500 dark:bg-green-700 text-green-100 dark:text-green-900">
              <span data-size="sm" className="circle bg-white" />
              <span className="font-caption text-white">Connected</span>
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 mt-3 font-caption">
          {project?.createdAt && (
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
              <Calendar className="w-3.5 h-3.5" />
              <span>
                Created{' '}
                {new Date(project.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            </div>
          )}
          <div data-size="sm" className="circle bg-gray-500" />
          <div className="flex items-center gap-1  text-gray-500 dark:text-gray-400">
            <GitHubLogo className="w-3.5 h-3.5" />
            <span>
              {project?.githubConfigs?.length} {project?.githubConfigs?.length === 1 ? 'repository' : 'repositories'}
            </span>
          </div>
          <div data-size="sm" className="circle bg-gray-500" />
          <div className="flex items-center gap-1  text-gray-500 dark:text-gray-400">
            <List className="w-3.5 h-3.5" />
            <span>
              {page.ticketCount} {page.ticketCount === 1 ? 'ticket' : 'tickets'}
            </span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex mt-4 items-center gap-1 tab-group">
        <button
          onClick={() => page.setActiveTab('tasks')}
          className={`tab font-body-base  ${page.activeTab === 'tasks' ? 'active' : ''}`}
        >
          Tickets
        </button>
        <button
          onClick={() => page.setActiveTab('config')}
          className={`tab font-body-base  ${page.activeTab === 'config' ? 'active' : ''}`}
        >
          Configuration
        </button>
        {project?.description && (
          <button
            onClick={() => page.setActiveTab('details')}
            className={`tab font-body-base  ${page.activeTab === 'details' ? 'active' : ''}`}
          >
            Details
          </button>
        )}
      </div>

      {/* Tickets Tab */}
      {page.activeTab === 'tasks' && project?.id && (
        <>
          {page.isAdmin && (
            <div className="flex justify-end mb-4">
              <ManualImportTicket
                onImport={actions.manualImport}
                isSubmitting={mutations.isImportingTicket}
              />
            </div>
          )}
          <TicketsTable
            tickets={tickets.items}
            projectId={project.id}
            showCost={page.isAdmin}
            isLoadingMore={tickets.isLoadingMore}
            isSearching={tickets.isSearching}
            hasMore={tickets.hasMore}
            sentinelRef={tickets.sentinelRef}
            searchQuery={tickets.searchQuery}
            onSearchChange={tickets.onSearchChange}
            onCancelTask={actions.cancelTask}
          />
        </>
      )}
      {project?.description && page.activeTab === 'details' && project?.id && (
        <Card>
          <div className="flex justify-between items-center gap-1">
            <p className="text-[1rem] font-semibold leading-[140%] dark:text-white">Details</p>
          </div>
          <div className="mt-3 font-body-base text-gray-500 dark:text-white-500">{project?.description}</div>
        </Card>
      )}

      {/* Configuration Tab */}
      {page.activeTab === 'config' && project && (
        <>
          {/* Knowledge Base entry point (replaces free-text agent context editing) */}
          <AgentContextCard projectId={project.id} pinnedCount={page.pinnedKnowledgeCount} />

          {/* GitHub Configuration - Full Width */}
          <GitHubConfigCard
            githubConfigs={project.githubConfigs}
            githubIntegrationStatus={integrations.githubStatus}
            isAdmin={page.isAdmin}
            isLoadingRepos={addRepo.isLoadingRepos || mutations.isAddingRepo}
            showAddRepoModal={addRepo.isOpen}
            availableRepos={addRepo.availableRepos}
            selectedRepoToAdd={addRepo.selectedRepoToAdd}
            repoToDelete={confirmations.repoToDelete}
            onOpenAddRepoModal={addRepo.open}
            onCloseAddRepoModal={addRepo.close}
            onSelectRepo={addRepo.setSelectedRepoToAdd}
            onAddRepo={actions.addRepo}
            onDeleteRepo={actions.deleteRepo}
            onSetRepoToDelete={confirmations.setRepoToDelete}
            onEditBranch={editBranch.open}
          />

          {/* Jira Configuration */}
          <JiraConfigCard
            jiraConfig={project.jiraConfig}
            jiraIntegrationStatus={integrations.jiraStatus}
            isAdmin={page.isAdmin}
            showEditModal={jira.isOpen}
            isLoadingUsers={jira.isLoadingJiraUsers}
            isLoadingStatuses={jira.isLoadingJiraStatuses}
            isSaving={mutations.isSavingJiraConfig}
            jiraAssignableUsers={jira.jiraAssignableUsers}
            jiraStatuses={jira.jiraStatuses}
            selectedAssigneeAccountId={jira.selectedAssigneeAccountId}
            selectedStartStatus={jira.selectedStartStatus}
            selectedEndStatus={jira.selectedEndStatus}
            validationError={jira.validationError}
            onOpenEditModal={jira.open}
            onCloseEditModal={jira.close}
            onSave={actions.saveJiraConfig}
            onSetSelectedAssigneeAccountId={jira.setSelectedAssigneeAccountId}
            onSetSelectedStartStatus={jira.setSelectedStartStatus}
            onSetSelectedEndStatus={jira.setSelectedEndStatus}
            hasChanges={
              jira.selectedAssigneeAccountId !== jira.originalAssigneeAccountId ||
              jira.selectedStartStatus !== jira.originalStartStatus ||
              jira.selectedEndStatus !== jira.originalEndStatus
            }
          />

          {/* Slack Configuration */}
          <SlackConfigCard
            slackConfig={page.slackConfig}
            isAdmin={page.isAdmin}
            showModal={slack.isOpen}
            showDeleteConfirm={confirmations.slackToDelete}
            slackWebhookUrl={slack.slackWebhookUrl}
            isSaving={mutations.isSavingSlack}
            hasChanges={slack.slackWebhookUrl !== slack.originalSlackWebhookUrl}
            onOpenModal={slack.open}
            onCloseModal={slack.close}
            onSetSlackWebhookUrl={slack.setSlackWebhookUrl}
            onSave={actions.saveSlack}
            onDelete={actions.deleteSlack}
            onSetShowDeleteConfirm={confirmations.setSlackToDelete}
          />
        </>
      )}

      {/* Edit Default Branch Modal */}
      <EditBranchModal
        isOpen={editBranch.isOpen}
        repoFullName={editBranch.config?.repoFullName || ''}
        branches={editBranch.branches}
        selectedBranch={editBranch.selectedBranch}
        isLoadingBranches={editBranch.isLoadingBranches}
        isSaving={mutations.isSavingBranch}
        hasChanges={editBranch.selectedBranch !== editBranch.config?.defaultBranch}
        onClose={editBranch.close}
        onSelectBranch={editBranch.setSelectedBranch}
        onSave={actions.saveBranch}
      />

      {/* Toast Notifications */}
      <Toast
        isOpen={toast.state.isOpen}
        onClose={toast.close}
        message={toast.state.message}
        variant={toast.state.variant}
      />
    </div>
  );
}
